import ReCAPTCHA from "react-google-recaptcha"
import { useRef, useState, useEffect } from "react"
import { useForm, FormProvider } from "react-hook-form"
import { useMediaPredicate } from "react-media-hook"
import { useRouter } from "next/router"
import Image from "next/image"
import classNames from "classnames"
import { usePathData, useSiteContext } from "@/lib/contexts/appContext"
import { pushDataLayer } from "@/lib/helpers/tagManager"
import { createGravityFormEntry } from "@/lib/api/gravityForms"
import { useGravityForm } from "@/lib/hooks/useGravityForm"
import Form from "@/components/gravity-forms/Form"
import Spinner from "@/components/common/logos/spinner"
import Block from "@/components/wrappers/block"
import styles from "./gravity-form.module.css"

export default function GravityForm({ block, settings }) {
  const router = useRouter()
  const { template } = usePathData()
  const context = useSiteContext()
  const isArticle = template === "article"
  const { parentBlock, objectId, inSidebar } = block.attrs
  const form = useGravityForm(block?.attrs?.form)

  const methods = useForm({ mode: "onTouched" })
  const { formState, handleSubmit, setError, reset } = methods

  const [currentPage, setCurrentPage] = useState(1)
  const [result, setResult] = useState(null)
  const [uploadFiles, setUploadFiles] = useState({})
  const [hasMounted, setMounted] = useState(false)
  const isNarrow = useMediaPredicate("(max-width: 1024px)")

  const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY
  const recaptchaRef = useRef()
  const formRef = useRef()

  const onSubmit = async (formData) => {
    formData.objectId = objectId
    formData.parentBlock = parentBlock

    const token = await recaptchaRef.current.executeAsync()
    const response = await createGravityFormEntry(
      context,
      form.id,
      formData,
      token
    )
    const { is_valid } = response.data

    const header = document.getElementById("header")
    const formTop = formRef.current.offsetTop

    window.scrollTo({
      top: formTop - header.offsetHeight - 30,
      behavior: "smooth"
    })

    if (is_valid) {
      pushDataLayer({
        event: "form_submit",
        formId: form?.id,
        formTitle: form?.title,
        formType: form?.formType,
        formData: formData
      })

      // Don't repaint this component if we redirect
      if (response.data.confirmation_type === "redirect") {
        router.push(response.data.confirmation_redirect)
        return
      }

      // Let's repaint the component with result data (if not in resultblock)
      setResult(response.data)
      return
    }

    setResult(null)
    for (const key in response.data.validation_messages) {
      setError(`input_${key}`, {
        type: "manual",
        message:
          response.data.validation_messages[key] ??
          "Fel uppstod. Kontrollera detta fält."
      })
    }
  }

  useEffect(() => {
    let resetButton = null

    if (formState.isSubmitSuccessful) {
      resetButton = document.querySelector(".confirmation button[type=reset]")
      if (resetButton) {
        resetButton.addEventListener("click", handleResetButtonClick)
      }
    }

    return () => {
      if (resetButton) {
        resetButton.removeEventListener("click", handleResetButtonClick)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isSubmitSuccessful])

  useEffect(() => {
    let isMounted = true
    if (isMounted) {
      setMounted(true)
    }
    return () => {
      isMounted = false
    }
  }, [])

  const handleResetButtonClick = () => {
    setCurrentPage(1)
    setResult(null)
    setUploadFiles({})

    const header = document.getElementById("header")
    const formTop = formRef.current.offsetTop
    window.scrollTo({
      top: formTop - header.offsetHeight - 30,
      behavior: "smooth"
    })

    reset()
  }

  if (!form) {
    return null
  }

  if (!form.fields || form?.fields?.length === 0) {
    return null
  }

  const { cssClass } = form
  const hasGrid =
    form?.quiz?.hasImages && !form?.quiz?.multiplePerPage && !isArticle

  const blockClasses = [
    styles.gravityForm,
    cssClass,
    {
      "is-article": isArticle,
      "has-grid": hasGrid
    }
  ]

  let width = hasGrid || form?.quiz?.multiplePerPage ? "full" : "column"

  if (inSidebar) {
    width = "bleed"
  }

  if (settings?.width) {
    width = settings.width
  }

  if (form?.offSchedule) {
    if (!form?.message) {
      return null
    }

    return (
      <Block
        width={width}
        block={block}
        blockRef={formRef}
        className={classNames(blockClasses)}>
        <p>{form?.message}</p>
      </Block>
    )
  }

  return (
    <FormProvider {...methods}>
      <Block
        width={width}
        block={block}
        blockRef={formRef}
        className={classNames(blockClasses)}>
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <ReCAPTCHA
            className="hidden"
            ref={recaptchaRef}
            size="invisible"
            sitekey={siteKey}
          />
          {!formState.isSubmitSuccessful && (
            <Form
              block={block}
              formRef={formRef}
              form={form}
              inSidebar={inSidebar}
              isArticle={isArticle}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              uploadFiles={uploadFiles}
              setUploadFiles={setUploadFiles}
            />
          )}
          {formState.isSubmitSuccessful && (
            <Confirmation result={result} inSidebar={inSidebar} />
          )}
          <Media
            form={form}
            isArticle={isArticle}
            hasMounted={hasMounted}
            isNarrow={isNarrow}
            currentPage={currentPage}
          />
        </form>
      </Block>
    </FormProvider>
  )
}

const Media = ({ form, isArticle, hasMounted, isNarrow, currentPage }) => {
  if (isArticle || isNarrow || !hasMounted) {
    return null
  }

  if (!form?.quiz?.hasImages || form?.quiz?.multiplePerPage) {
    return null
  }

  const field = form.fields
    .filter((item) => item?.imageData && item.pageNumber === currentPage)
    .shift()

  if (!field?.imageData.url) {
    return null
  }

  return (
    <div
      className={classNames("extra-content", {
        "is-4-3": form?.quiz?.imageFormat === "4:3",
        "is-16-9": form?.quiz?.imageFormat === "16:9",
        "is-square": form?.quiz?.imageFormat === "square"
      })}>
      <figure>
        <div className="spinner">
          <Spinner className="w-8 h-8 opacity-25" />
        </div>
        <Image
          src={field.imageData.url}
          alt={field.imageData.alt}
          quality="50"
          height={field.imageData.height}
          width={field.imageData.width}
          sizes="(max-width: 768px) 100vw, 50vw"
        />
      </figure>
    </div>
  )
}

const Confirmation = ({ result, inSidebar }) => {
  const router = useRouter()

  if (result === null) {
    return null
  }

  let html = result.confirmation_message
  const url = encodeURI(window.location.origin + router.asPath)

  html = html.replace(
    "%%facebook%%",
    "https://www.facebook.com/share.php?u=" + url
  )
  html = html.replace(
    "%%twitter%%",
    "https://twitter.com/intent/tweet?url=" + url
  )

  const classes = [
    "bg-white rounded py-6 confirmation",
    { "px-6 md:px-7.5 px-0": inSidebar !== true }
  ]

  return (
    <div
      className={classNames(classes)}
      dangerouslySetInnerHTML={{
        __html: html
      }}></div>
  )
}
